"use client";

/**
 * Editar perfil 5.2 — nombre, WhatsApp (solo lectura) y correo.
 */

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";

import { BottomNav, HeaderTitulo } from "@/components/app/chrome";
import { useApp } from "@/components/app/proveedor";
import { IconoLapiz } from "@/components/app/iconos";

export default function PaginaEditar() {
  const router = useRouter();
  const { usuario, refrescarUsuario } = useApp();
  const [nombre, setNombre] = useState("");
  const [email, setEmail] = useState("");
  const [estado, setEstado] = useState<"idle" | "guardando" | "listo">("idle");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (usuario === null) router.replace("/acceso?next=/cuenta/editar");
    if (usuario) {
      setNombre((n) => n || usuario.nombre);
      setEmail((e) => e || (usuario.email ?? ""));
    }
  }, [usuario, router]);

  async function guardar() {
    if (estado === "guardando") return;
    setError(null);
    setEstado("guardando");
    try {
      const res = await fetch("/api/auth/me", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nombre, email }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        setError(data.error ?? "No pudimos guardar.");
        setEstado("idle");
        return;
      }
      await refrescarUsuario();
      setEstado("listo");
      setTimeout(() => router.push("/cuenta"), 700);
    } catch {
      setError("Sin conexión. Intenta de nuevo.");
      setEstado("idle");
    }
  }

  const inicial = (nombre || usuario?.nombre || "F").charAt(0).toUpperCase();
  const inputCls =
    "h-[58px] w-full rounded-[20px] border border-black/10 bg-white px-[18px] text-base outline-0 placeholder:text-tinta/40";

  return (
    <>
      <HeaderTitulo titulo="Editar perfil" href="/cuenta" />
      <main className="anim-bz-in min-h-(--alto-app) px-6 pt-[100px] pb-[62px]">
        <div className="flex flex-col gap-3.5 pt-[22px] pb-[30px]">
          <div className="flex justify-center">
            <div className="relative">
              <span className="flex h-[88px] w-[88px] items-center justify-center rounded-[30px] bg-lima font-app text-[32px] font-black text-bosque">
                {inicial}
              </span>
              <span className="absolute right-[-4px] bottom-[-4px] flex h-[30px] w-[30px] items-center justify-center rounded-full bg-bosque">
                <IconoLapiz color="#fff" />
              </span>
            </div>
          </div>

          <label className="block">
            <span className="mb-1.5 block text-xs text-tinta/55">Nombre</span>
            <input
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Tu nombre"
              className={inputCls}
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs text-tinta/55">WhatsApp</span>
            <input
              value={usuario ? `+593 ${usuario.telefono}` : ""}
              readOnly
              className={`${inputCls} text-tinta/60`}
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs text-tinta/55">Correo</span>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@correo.com"
              type="email"
              className={inputCls}
            />
          </label>

          <div className="rounded-[20px] bg-humo p-4 text-[13px] leading-[19px] text-tinta/65">
            Tu número es tu llave de acceso. Si necesitas cambiarlo, escríbenos
            por WhatsApp para verificarlo.
          </div>

          {error && <p className="text-sm text-[#B4463A]">{error}</p>}

          <button
            type="button"
            onClick={() => void guardar()}
            disabled={estado === "guardando"}
            className="flex h-[62px] w-full items-center justify-center rounded-[30px] bg-verde font-app text-[17px] font-semibold text-white transition active:scale-[.98] disabled:opacity-60"
          >
            {estado === "listo"
              ? "¡Guardado!"
              : estado === "guardando"
                ? "Guardando…"
                : "Guardar cambios"}
          </button>
        </div>
      </main>
      <BottomNav />
    </>
  );
}
